import {
  IssueType,
  IssueTypeEnum,
  IssueTypeValues,
} from '@issuemanager/constants';

export type IssueTypeFieldMode = 'create' | 'edit';

const ISSUE_TYPE_SPECIFIC_FIELDS = [
  'classification',
  'rootCause',
  'managementResponse',
  'recommendations',
  'resolution',
  'includeInAuditReport',
  'reviewStatus',
  'datePublished',
  'remediation',
] as const;

export type IssueTypeSpecificField =
  (typeof ISSUE_TYPE_SPECIFIC_FIELDS)[number];

export type IssueTypeFieldConfigEntry = Record<
  IssueTypeFieldMode,
  readonly IssueTypeSpecificField[]
>;

const STANDARD_ISSUE_FIELD_CONFIG: IssueTypeFieldConfigEntry = {
  create: [],
  edit: [],
};

export const ISSUE_TYPE_FIELD_CONFIG = {
  [IssueTypeEnum.AUDIT_FINDING]: {
    create: [
      'classification',
      'rootCause',
      'recommendations',
      'includeInAuditReport',
    ],
    edit: [
      'classification',
      'rootCause',
      'managementResponse',
      'recommendations',
      'resolution',
      'includeInAuditReport',
      'reviewStatus',
      'datePublished',
    ],
  },
  [IssueTypeEnum.CONTROL_DEFICIENCY]: {
    create: ['classification', 'rootCause'],
    edit: [
      'classification',
      'rootCause',
      'managementResponse',
      'resolution',
      'remediation',
    ],
  },
  [IssueTypeEnum.ISSUE]: STANDARD_ISSUE_FIELD_CONFIG,
  [IssueTypeEnum.INCIDENT]: STANDARD_ISSUE_FIELD_CONFIG,
  [IssueTypeEnum.EXCEPTION]: STANDARD_ISSUE_FIELD_CONFIG,
} as const satisfies Record<IssueType, IssueTypeFieldConfigEntry>;

export function isValidIssueType(value: unknown): value is IssueType {
  return (
    typeof value === 'string' && IssueTypeValues.includes(value as IssueType)
  );
}

export function isIssueTypeSpecificField(
  field: unknown,
): field is IssueTypeSpecificField {
  if (typeof field !== 'string') {
    return false;
  }
  return ISSUE_TYPE_SPECIFIC_FIELDS.includes(field as IssueTypeSpecificField);
}

export function getAllowedFieldsForIssueType(
  issueType: unknown,
  mode: IssueTypeFieldMode,
): readonly IssueTypeSpecificField[] {
  if (!isValidIssueType(issueType)) {
    return [];
  }

  return ISSUE_TYPE_FIELD_CONFIG[issueType][mode];
}
